package scenes

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import rendering.AnimatedBackground
import rendering.fontMono
import kotlin.math.sin
import kotlin.random.Random

private val COLOR_BG = Color.Black
private val COLOR_SELECTED_BORDER = Color(0xFF00E5FF)
private val COLOR_SELECTED_TEXT = Color(0xFF00E5FF)
private val COLOR_UNSELECTED_BORDER = Color(0xFF333355)
private val COLOR_UNSELECTED_TEXT = Color(0xFF666688)

private data class MenuItem(val label: String, val action: () -> Unit)

// ── Logo constants ────────────────────────────────────────────────────────────

private const val LOGO_CELL = 13f   // dp
private const val LOGO_GAP  = 2f    // dp
private const val LOGO_STEP = LOGO_CELL + LOGO_GAP  // 15f dp
private const val LOGO_LGAP = 14f   // dp between letters

// ── Letter piece data ─────────────────────────────────────────────────────────

private data class LogoPieceData(
    val colorId: Int,
    val cells: List<Pair<Int, Int>>,   // (row, col) offsets within the piece
    val worldX: Float,                  // dp
    val worldY: Float,                  // dp
)

private fun buildLogoPieces(): List<LogoPieceData> {
    data class PL(val colorId: Int, val cells: List<Pair<Int, Int>>, val dr: Int, val dc: Int)

    val layouts: Map<Char, List<PL>> = mapOf(
        'G' to listOf(
            PL(21, listOf(0 to 0, 0 to 1, 0 to 2, 0 to 3), 0, 0),
            PL(7,  listOf(0 to 0, 0 to 1, 1 to 1, 2 to 1), 3, 2),
            PL(7,  listOf(0 to 0, 0 to 1, 0 to 2, 1 to 2), 4, 0),
        ),
        'R' to listOf(
            PL(9,  listOf(0 to 0, 1 to 0, 2 to 0, 3 to 0, 4 to 0), 0, 0),
            PL(7,  listOf(0 to 0, 0 to 1, 1 to 1, 2 to 1), 0, 1),
            PL(7,  listOf(0 to 0, 0 to 1, 0 to 2, 1 to 2), 3, 1),
        ),
        'A' to listOf(
            PL(12, listOf(0 to 1, 0 to 2, 1 to 0, 1 to 1, 2 to 0), 0, 1),
            PL(18, listOf(0 to 1, 1 to 0, 1 to 1, 2 to 1, 3 to 1), 1, 2),
            PL(1,  listOf(0 to 0, 1 to 0, 2 to 0, 3 to 0), 1, 0),
        ),
        'V' to listOf(
            PL(11, listOf(0 to 0, 1 to 0, 2 to 0, 2 to 1, 3 to 1), 0, 0),
            PL(24, listOf(0 to 1, 1 to 1, 2 to 0, 2 to 1, 3 to 0), 0, 2),
        ),
        'I' to listOf(
            PL(-2, listOf(0 to 0, 0 to 1, 0 to 2), 0, 0),
            PL(-2, listOf(0 to 0, 1 to 0, 2 to 0), 1, 1),
            PL(-2, listOf(0 to 0, 0 to 1, 0 to 2), 4, 0),
        ),
        'T' to listOf(
            PL(13, listOf(0 to 0, 0 to 1, 0 to 2, 1 to 1, 2 to 1), 0, 0),
            PL(-2, listOf(0 to 0, 1 to 0, 2 to 0), 3, 1),
        ),
    )

    val letterCols = mapOf('I' to 3, 'T' to 3)
    val letterRows = mapOf('I' to 5, 'V' to 4)
    val maxRows = 6
    val word = "GRAVITAR"

    val pieces = mutableListOf<LogoPieceData>()
    var curX = 0f

    word.forEach { ch ->
        val cols = letterCols[ch] ?: 4
        val rows = letterRows[ch] ?: maxRows
        val rowOffset = (maxRows - rows) / 2

        layouts[ch]?.forEach { pl ->
            pieces += LogoPieceData(
                colorId = pl.colorId,
                cells   = pl.cells,
                worldX  = curX + pl.dc * LOGO_STEP,
                worldY  = (pl.dr + rowOffset) * LOGO_STEP,
            )
        }
        curX += (cols * LOGO_STEP - LOGO_GAP) + LOGO_LGAP
    }

    return pieces
}

// ── Color lookup ──────────────────────────────────────────────────────────────

private fun logoColor(colorId: Int): Color {
    val hex = domain.COLORS[colorId] ?: 0x00e5ff
    val r = ((hex shr 16) and 0xFF) / 255f
    val g = ((hex shr 8)  and 0xFF) / 255f
    val b = (hex and 0xFF) / 255f
    return Color(r, g, b, 0.93f)
}

// ── Falling piece state ───────────────────────────────────────────────────────

private data class FallingState(
    val piece: LogoPieceData,
    val startX: Float,
    val startY: Float,
    val targetX: Float,
    val targetY: Float,
    val speed: Float,           // dp/frame at 60fps
    var currentX: Float,
    var currentY: Float,
    var landed: Boolean = false,
    var flashAlpha: Float = 0.93f,
)

// ── TitleLogo composable ──────────────────────────────────────────────────────

@Composable
fun TitleLogo(modifier: Modifier = Modifier) {
    val pieces = remember { buildLogoPieces() }

    val logoW = remember {
        var totalW = 0f
        val word = "GRAVITAR"
        val letterCols = mapOf('I' to 3, 'T' to 3)
        word.forEachIndexed { i, ch ->
            val cols = letterCols[ch] ?: 4
            totalW += cols * LOGO_STEP - LOGO_GAP
            if (i < word.length - 1) totalW += LOGO_LGAP
        }
        totalW
    }
    val logoH = remember { 6 * LOGO_STEP - LOGO_GAP }

    val shuffledPieces = remember { pieces.shuffled() }

    val fallingStates = remember {
        shuffledPieces.map { p ->
            val sx = p.worldX + (Random.nextFloat() - 0.5f) * 120f
            val sy = -LOGO_CELL * 5 - Random.nextFloat() * 200f
            FallingState(
                piece    = p,
                startX   = sx,
                startY   = sy,
                targetX  = p.worldX,
                targetY  = p.worldY,
                speed    = 4f + Random.nextFloat() * 4.5f,
                currentX = sx,
                currentY = sy,
            )
        }.toMutableList()
    }

    var spawnIndex by remember { mutableStateOf(0) }
    var spawnTimer by remember { mutableStateOf(0f) }
    val spawnInterval = 90f  // ms

    var allLanded by remember { mutableStateOf(false) }
    var pulseTime by remember { mutableStateOf(0f) }

    var frameToken by remember { mutableLongStateOf(0L) }
    LaunchedEffect(Unit) {
        var lastTime = withFrameMillis { it }
        while (true) {
            val now = withFrameMillis { it }
            val delta = (now - lastTime).toFloat().coerceAtMost(100f)
            lastTime = now
            frameToken = now

            // Spawn pieces
            if (spawnIndex < fallingStates.size) {
                spawnTimer += delta
                if (spawnTimer >= spawnInterval) {
                    spawnTimer -= spawnInterval
                    spawnIndex = (spawnIndex + 1).coerceAtMost(fallingStates.size)
                    if (Random.nextFloat() < 0.3f && spawnIndex < fallingStates.size) {
                        spawnIndex = (spawnIndex + 1).coerceAtMost(fallingStates.size)
                    }
                }
            }

            // Update active pieces
            val dt = delta / 16f
            for (i in 0 until spawnIndex) {
                val fs = fallingStates[i]
                if (fs.landed) continue
                fs.currentX += (fs.targetX - fs.currentX) * 0.07f * dt
                fs.currentY += fs.speed * dt
                if (fs.currentY >= fs.targetY - 1f) {
                    fs.currentX = fs.targetX
                    fs.currentY = fs.targetY
                    fs.landed = true
                    fs.flashAlpha = 1.0f
                }
            }

            // Decay flash alpha for landed pieces
            for (fs in fallingStates) {
                if (fs.landed && fs.flashAlpha > 0.93f) {
                    fs.flashAlpha = (fs.flashAlpha - delta * 0.003f).coerceAtLeast(0.93f)
                }
            }

            // Check all landed
            if (!allLanded && spawnIndex >= fallingStates.size && fallingStates.all { it.landed }) {
                allLanded = true
            }

            // Pulse
            if (allLanded) {
                pulseTime += delta * 0.001f
            }
        }
    }

    val pulseScale = if (allLanded) 1f + sin(pulseTime * 1.4f).toFloat() * 0.018f else 1f

    Box(
        modifier = modifier
            .size(width = logoW.dp, height = logoH.dp)
            .graphicsLayer {
                scaleX = pulseScale
                scaleY = pulseScale
            },
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            @Suppress("UNUSED_EXPRESSION")
            frameToken

            for (i in 0 until spawnIndex) {
                val fs = fallingStates[i]
                val color = logoColor(fs.piece.colorId).copy(alpha = fs.flashAlpha)
                // Draw each cell at LOGO_STEP size (not LOGO_CELL) so adjacent cells
                // within the same piece are seamless — no gap lines between blocks.
                val cellPx = LOGO_STEP.dp.toPx()
                for ((r, c) in fs.piece.cells) {
                    val x = (fs.currentX + c * LOGO_STEP).dp.toPx()
                    val y = (fs.currentY + r * LOGO_STEP).dp.toPx()
                    drawRect(
                        color = color,
                        topLeft = Offset(x, y),
                        size = Size(cellPx, cellPx),
                    )
                }
            }
        }
    }
}

@Composable
fun MenuScene(navigate: Navigate) {
    val focusRequester = remember { FocusRequester() }
    var selectedIndex by remember { mutableStateOf(0) }

    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.1.sp)

    val items = remember(navigate) {
        listOf(
            MenuItem("PLAY") { navigate(SceneName.PLAY, null) },
            MenuItem("VERSUS") { /* stub */ },
            MenuItem("SETTINGS") { navigate(SceneName.SETTINGS, null) },
        )
    }

    LaunchedEffect(Unit) {
        delay(50)
        focusRequester.requestFocus()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(COLOR_BG)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                when (event.key) {
                    Key.DirectionUp -> {
                        selectedIndex = (selectedIndex - 1 + items.size) % items.size
                        true
                    }
                    Key.DirectionDown -> {
                        selectedIndex = (selectedIndex + 1) % items.size
                        true
                    }
                    Key.Enter -> {
                        items[selectedIndex].action()
                        true
                    }
                    else -> false
                }
            },
    ) {
        // Background rendered with pointer pass-through so it doesn't block clicks
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            // Title — scale to fit screen width with horizontal padding
            BoxWithConstraints(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center,
            ) {
                val availableWidth = maxWidth - 32.dp  // 16dp padding each side
                val logoNaturalWidth = run {
                    var totalW = 0f
                    val word = "GRAVITAR"
                    val letterCols = mapOf('I' to 3, 'T' to 3)
                    word.forEachIndexed { i, ch ->
                        val cols = letterCols[ch] ?: 4
                        totalW += cols * LOGO_STEP - LOGO_GAP
                        if (i < word.length - 1) totalW += LOGO_LGAP
                    }
                    totalW
                }.dp
                val scale = (availableWidth / logoNaturalWidth).coerceAtMost(1f)
                Box(
                    modifier = Modifier.graphicsLayer {
                        scaleX = scale
                        scaleY = scale
                    },
                ) {
                    TitleLogo()
                }
            }

            Spacer(modifier = Modifier.height(48.dp))

            // Menu items
            items.forEachIndexed { index, item ->
                val isSelected = index == selectedIndex
                val offsetX by animateFloatAsState(
                    targetValue = if (isSelected) 8f else 0f,
                    animationSpec = spring(dampingRatio = 0.5f, stiffness = 300f),
                )

                val borderColor = if (isSelected) COLOR_SELECTED_BORDER else COLOR_UNSELECTED_BORDER
                val textColor = if (isSelected) COLOR_SELECTED_TEXT else COLOR_UNSELECTED_TEXT

                Box(
                    modifier = Modifier
                        .width(240.dp)
                        .graphicsLayer { translationX = offsetX }
                        .clickable { item.action() }
                        .border(width = 1.dp, color = borderColor)
                        .padding(horizontal = 24.dp, vertical = 14.dp),
                    contentAlignment = Alignment.CenterStart,
                ) {
                    Text(
                        text = item.label,
                        style = monoStyle.copy(
                            fontSize = 18.sp,
                            color = textColor,
                        ),
                    )
                }

                Spacer(modifier = Modifier.height(12.dp))
            }
        }
    }
}
